/**
 * Smoothly scrolls to the FAQ (or other collapsible) section whose
 * `data-faq-pill-name` attribute matches the text of a clicked pill
 * (`data-pill-name`) inside a Pills Navigation section.
 *
 * Sections opt in via the "Pill Name" setting; pills opt in automatically
 * whenever they have a title. No pixel offsets are hardcoded - any
 * currently sticky/fixed header bars are measured live so the scroll
 * lands below them on every breakpoint.
 *
 * Also keeps a single pill marked `active` at a time (first pill by
 * default), and on mobile - where the pill list scrolls horizontally -
 * snaps the clicked pill to the edge of that scroll container matching
 * the direction the user was browsing in: scrolling right/forward snaps
 * the clicked pill to the start (so the next pills stay visible after
 * it), scrolling left/backward snaps it to the end (so the previous
 * pills stay visible before it).
 */
(function () {
  if (window.__pillsNavigationScrollBound) return;
  window.__pillsNavigationScrollBound = true;

  var MOBILE_MEDIA_QUERY = "(max-width: 899px)";
  var pillsScrollState = new WeakMap();

  function normalize(text) {
    return (text || "").trim().replace(/\s+/g, " ").toLowerCase();
  }

  function setDefaultActivePill() {
    document.querySelectorAll(".wt-pills-navigation__pills").forEach(function (list) {
      if (list.querySelector(".wt-pills-navigation__pill-item.active")) return;
      var firstItem = list.querySelector(".wt-pills-navigation__pill-item");
      if (firstItem) firstItem.classList.add("active");
    });
  }

  function setActivePill(pillItem) {
    var list = pillItem.closest(".wt-pills-navigation__pills");
    if (!list) return;
    list.querySelectorAll(".wt-pills-navigation__pill-item.active").forEach(function (item) {
      if (item !== pillItem) item.classList.remove("active");
    });
    pillItem.classList.add("active");
  }

  // Tracks which way the user last dragged/scrolled each pill list by hand
  // (touch swipe, trackpad, mouse-wheel) so a click can snap to the matching
  // edge. Scroll movement made by our own snap animation is ignored via the
  // `programmatic` flag so it never overwrites the user's real gesture.
  function getPillsScrollState(list) {
    var state = pillsScrollState.get(list);
    if (state) return state;

    state = { lastScrollLeft: list.scrollLeft, direction: "right", programmatic: false };
    pillsScrollState.set(list, state);

    list.addEventListener(
      "scroll",
      function () {
        var currentScrollLeft = list.scrollLeft;
        if (!state.programmatic) {
          if (currentScrollLeft > state.lastScrollLeft) {
            state.direction = "right";
          } else if (currentScrollLeft < state.lastScrollLeft) {
            state.direction = "left";
          }
        }
        state.lastScrollLeft = currentScrollLeft;
      },
      { passive: true }
    );

    return state;
  }

  function initPillsScrollTracking() {
    document.querySelectorAll(".wt-pills-navigation__pills").forEach(getPillsScrollState);
  }

  function bringPillIntoViewOnMobile(pillItem) {
    if (!window.matchMedia(MOBILE_MEDIA_QUERY).matches) return;
    var list = pillItem.closest(".wt-pills-navigation__pills");
    if (!list) return;

    var state = getPillsScrollState(list);
    var align = state.direction === "left" ? "end" : "start";

    state.programmatic = true;
    pillItem.scrollIntoView({ behavior: "smooth", inline: align, block: "nearest" });

    var stopTrackingProgrammaticScroll = function () {
      state.programmatic = false;
      state.lastScrollLeft = list.scrollLeft;
    };

    if ("onscrollend" in window) {
      list.addEventListener("scrollend", stopTrackingProgrammaticScroll, { once: true });
    } else {
      clearTimeout(state.programmaticTimer);
      state.programmaticTimer = setTimeout(stopTrackingProgrammaticScroll, 500);
    }
  }

  function getStickyOffset() {
    var offset = 0;
    document
      .querySelectorAll(".page-header, .wt-pills-navigation")
      .forEach(function (el) {
        var position = getComputedStyle(el).position;
        if (position !== "fixed" && position !== "sticky") return;
        var rect = el.getBoundingClientRect();
        if (rect.bottom > offset && rect.top <= offset + 1) {
          offset = rect.bottom;
        }
      });
    return offset;
  }

  function findMatchingSection(pillName) {
    var target = normalize(pillName);
    if (!target) return null;
    var sections = document.querySelectorAll("[data-faq-pill-name]");
    for (var i = 0; i < sections.length; i++) {
      if (normalize(sections[i].getAttribute("data-faq-pill-name")) === target) {
        return sections[i];
      }
    }
    return null;
  }

  setDefaultActivePill();
  initPillsScrollTracking();

  document.addEventListener("click", function (event) {
    var pill = event.target.closest("[data-pill-name]");
    if (!pill) return;

    var section = findMatchingSection(pill.getAttribute("data-pill-name"));
    if (!section) return;

    event.preventDefault();

    var pillItem = pill.closest(".wt-pills-navigation__pill-item");
    if (pillItem) {
      setActivePill(pillItem);
      bringPillIntoViewOnMobile(pillItem);
    }

    var targetTop =
      section.getBoundingClientRect().top + window.scrollY - getStickyOffset();

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: "smooth",
    });
  });
})();
